import { useEffect, useState } from 'react';
import { ModalProps } from '../../../../../../../components/modal/modal';
import { useLang } from '../../../../../../../hooks/use-lang';
import { useShiftsStore } from '../../../../../../../store/use-shifts-store';
import dayjs from 'dayjs';
import { validate } from '../../../../../../../helpers/validate';
import { localWeekDayToIndexWeekday } from '../../../../../../../i18n/i18n';
import { tzOffset } from '../../../../../../../helpers/date-time';

const getDatesBetween = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs): dayjs.Dayjs[] => {
  const dates = [];
  let currentDate = startDate;

  while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
    dates.push(currentDate);
    currentDate = currentDate.add(1, 'day');
  }

  return dates;
};

export const useShiftsControlsScheduleModal = (props: ModalProps) => {
  const { t } = useLang();
  const { postShift, isLoading, shift, handleResetShift } = useShiftsStore();
  const [step, setStep] = useState(0);

  const handleNext = async () => {
    if (step === 0) {
      const isValid = validate({
        startDate: { rule: () => !!shift?.startDate },
        endDate: { rule: () => !!shift?.endDate },
      });
      if (!isValid) return;
    }
    if (step === 1) {
      const isValid = validate({ staffIds: { rule: () => !!shift?.staffIds?.length } });
      if (!isValid) return;
    }
    if (step === 2) {
      const isValid = validate({
        startTime: { rule: () => !!shift?.startTime },
        endTime: { rule: () => !!shift?.endTime },
      });
      if (!isValid) return;
      const start = dayjs(shift?.startDate);
      const end = dayjs(shift?.endDate);
      const dates = getDatesBetween(start, end);
      const filteredDates = dates.filter((date) =>
        shift?.weekDays?.map((item) => localWeekDayToIndexWeekday[item])?.includes(date.day()),
      );

      const doubleOffset = tzOffset * 2;
      const format = 'YYYY-MM-DD HH:mm:ss';
      const mappedDates = filteredDates.map((date) => {
        const formattedDate = date.format('YYYY-MM-DD'); // без разницы на какую дату смотреть, это для сравнения начала/завершения смены и определения ночная она или нет
        const startDate = dayjs(`${formattedDate}T${shift?.startTime}:00.000Z`);
        const endDate = dayjs(`${formattedDate}T${shift?.endTime}:00.000Z`);

        const isNightShift = startDate.isAfter(endDate);

        if (isNightShift) {
          const end_time = endDate.add(1, 'day').add(doubleOffset, 'minutes').format(format);
          const start_time = startDate.add(doubleOffset, 'minutes').format(format);
          return { start_time, end_time };
        }

        const start_time = startDate.add(doubleOffset, 'minutes').format(format);
        const end_time = endDate.add(doubleOffset, 'minutes').format(format);
        return { start_time, end_time };
      });
      
      const request = {
        staff_ids: shift?.staffIds || [],
        times: mappedDates,
      };
      await postShift(request);
      handleResetShift();
      props.onClose();
    }
    setStep((prev) => prev + 1);
  };

  useEffect(() => {
    return () => handleResetShift();
  }, []);

  const handlePrev = () => {
    if (step === 0) {
      props.onClose();
    } else {
      setStep((prev) => prev - 1);
    }
  };

  return {
    t,
    handleNext,
    step,
    handlePrev,
    isLoading,
  };
};
